import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Avatar,
  Typography,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  Toolbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Card,
  CardContent,
  Divider,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  CheckCircle as OnlineIcon,
  Cancel as OfflineIcon,
  DirectionsCar as RidesIcon,
  CardGiftcard as LoyaltyIcon,
  Savings as MilesIcon,
  Phone as PhoneIcon,
  Smartphone as SmartphoneIcon,
  Block as BlockIcon,
  CheckCircleOutline as UnblockIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import {
  fetchCustomers,
  deleteCustomer,
  toggleCustomerBlocked,
  startCustomersStatusListener,
  stopCustomersStatusListener
} from '../../redux/slices/customersSlice';
import { fetchSettings } from '../../redux/slices/settingsSlice';
import { isCentralRole, isOperatorAdminRole } from '../../redux/slices/authSlice';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';
import DeleteConfirmDialog from '../common/DeleteConfirmDialog';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const RidesBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  '& .MuiSvgIcon-root': {
    color: theme.palette.info.main,
    fontSize: '1.2rem',
  },
}));

// Einmalig erstellter Formatter – nicht bei jedem formatDate()-Aufruf neu instanziieren
const DATE_FORMATTER = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const CustomersList = () => {
  const dispatch = useDispatch();
  const { customers, isLoading, error } = useSelector(state => state.customers);
  const { global: globalSettings } = useSelector(state => state.settings);
  const currentUser = useSelector(state => state.auth.user);

  // Treueprogramm-Einstellungen (genau eines aktiv): 'off' | 'stamp' | 'miles'
  const programMode = globalSettings?.loyaltyProgramMode
    || (globalSettings?.loyaltyCardActive ? 'stamp' : 'off');
  const ridesRequired = globalSettings?.loyaltyCardRidesRequired || 5;
  // Wie in CustomerDetails/Server: milesPerBlock <= 0 = Einlöse-Schwelle nicht
  // konfiguriert → keine Blöcke berechnen (kein Fallback auf 50, sonst Phantom-Blöcke).
  const milesPerBlock = globalSettings?.milesPerBlock || 0;

  // Spaltentitel je nach aktivem Programm
  const loyaltyColumnLabel = programMode === 'miles'
    ? 'Treuemeilen'
    : programMode === 'stamp'
      ? 'Treuekarte'
      : 'Treueprogramm';

  // Kompakte Fortschrittsanzeige des aktiven Treueprogramms je Kunde.
  // Ist kein Programm aktiv, wird nur ein Hinweis-Chip gezeigt (kein Stand).
  const renderLoyaltyStatus = (customer) => {
    // Telefonkunden nehmen am Treueprogramm nicht teil (Cloud Functions überspringen
    // isPhoneCustomer). Statt eines irreführenden 0-Stands ein neutraler Hinweis.
    if (customer.user?.isPhoneCustomer === true) {
      return (
        <Chip size="small" variant="outlined" label="Nur App-Kunden" color="default" />
      );
    }
    if (programMode === 'stamp') {
      const count = customer.loyaltyCard?.currentRideCount || 0;
      const available = customer.loyaltyCard?.availableDiscounts || 0;
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5,
            borderRadius: 1, bgcolor: 'primary.light', color: 'primary.contrastText'
          }}>
            <LoyaltyIcon sx={{ fontSize: 16 }} />
            <Typography variant="body2" fontWeight="medium">{count}/{ridesRequired}</Typography>
          </Box>
          {available > 0 && (
            <Chip size="small" label={`${available}x`} color="success" sx={{ height: 22, fontSize: '0.75rem' }} />
          )}
        </Box>
      );
    }
    if (programMode === 'miles') {
      const balance = customer.milesCard?.balance || 0;
      const blocks = milesPerBlock > 0 ? Math.floor(balance / milesPerBlock) : 0;
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5,
            borderRadius: 1, bgcolor: 'primary.light', color: 'primary.contrastText'
          }}>
            <MilesIcon sx={{ fontSize: 16 }} />
            <Typography variant="body2" fontWeight="medium">{balance} Meilen</Typography>
          </Box>
          {blocks > 0 && (
            <Chip size="small" label={`${blocks}x`} color="success" sx={{ height: 22, fontSize: '0.75rem' }} />
          )}
        </Box>
      );
    }
    // Kein Treueprogramm aktiv
    return (
      <Chip size="small" variant="outlined" label="Kein Programm aktiv" color="default" />
    );
  };

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [onlineFilter, setOnlineFilter] = useState('all');
  const [customerTypeFilter, setCustomerTypeFilter] = useState('all');
  const [blockedFilter, setBlockedFilter] = useState('active');
  const [sortBy, setSortBy] = useState('name');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuCustomer, setMenuCustomer] = useState(null);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);
  const [bulkActionType, setBulkActionType] = useState(null); // 'delete' | 'block' | 'unblock'
  const [bulkConfirmDialogOpen, setBulkConfirmDialogOpen] = useState(false);
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  
  // Flag, um zu verhindern, dass useEffect mehrmals ausgeführt wird
  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    dispatch(fetchSettings());

    if (!dataLoaded) {
      const loadCustomers = async () => {
        try {
          await dispatch(fetchCustomers({ simplified: true, forceRefresh: false }));
          setDataLoaded(true);
          dispatch(startCustomersStatusListener());
        } catch (error) {
          console.error("CustomersList: Fehler beim Laden der Kundendaten:", error);
          setDataLoaded(false);
        }
      };
      loadCustomers();
    }

    return () => {
      dispatch(stopCustomersStatusListener());
    };
  }, [dispatch, dataLoaded]);

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleSearch = useCallback((event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  }, []);

  const handleOnlineFilterChange = useCallback((event) => {
    setOnlineFilter(event.target.value);
    setPage(0);
  }, []);

  const handleCustomerTypeFilterChange = useCallback((event) => {
    setCustomerTypeFilter(event.target.value);
    setPage(0);
  }, []);

  const handleBlockedFilterChange = useCallback((event) => {
    setBlockedFilter(event.target.value);
    setPage(0);
  }, []);

  const handleSortByChange = useCallback((event) => {
    setSortBy(event.target.value);
    setPage(0);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setMenuAnchor(null);
    setMenuCustomer(null);
  }, []);

  const handleOpenMenu = useCallback((event, customer) => {
    setMenuAnchor(event.currentTarget);
    setMenuCustomer(customer);
  }, []);

  const handleDeleteClick = useCallback((customer) => {
    setSelectedCustomer(customer);
    setDeleteDialogOpen(true);
    setMenuAnchor(null);
    setMenuCustomer(null);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (selectedCustomer) {
      dispatch(deleteCustomer(selectedCustomer.id));
      setDeleteDialogOpen(false);
      setSelectedCustomer(null);
    }
  }, [dispatch, selectedCustomer]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false);
    setSelectedCustomer(null);
  }, []);

  const handleToggleBlocked = useCallback((customer) => {
    dispatch(toggleCustomerBlocked({ id: customer.id, isBlocked: !customer.isBlocked }));
    setMenuAnchor(null);
    setMenuCustomer(null);
  }, [dispatch]);

  const canManageCustomer = useCallback((customer) => {
    if (isCentralRole(currentUser)) return true;
    const managingOperatorId = customer?.user?.managedByOperatorId ||
      customer?.user?.createdByOperatorId ||
      customer?.user?.operatorId;
    return isOperatorAdminRole(currentUser) &&
      customer?.user?.isPhoneCustomer === true &&
      managingOperatorId === currentUser?.operatorId;
  }, [currentUser]);

  const canManageSelectedCustomers = useMemo(() => {
    if (!selectedCustomerIds.length) return false;
    const selectedCustomers = customers.filter(customer => selectedCustomerIds.includes(customer.id));
    return selectedCustomers.length > 0 && selectedCustomers.every(canManageCustomer);
  }, [canManageCustomer, customers, selectedCustomerIds]);

  const handleToggleCustomerSelect = useCallback((customerId) => {
    setSelectedCustomerIds(prev =>
      prev.includes(customerId)
        ? prev.filter(id => id !== customerId)
        : [...prev, customerId]
    );
  }, []);

  const handleBulkAction = useCallback((type) => {
    setBulkActionType(type);
    setBulkConfirmDialogOpen(true);
  }, []);

  const handleCloseBulkDialog = useCallback(() => {
    if (isBulkProcessing) return;
    setBulkConfirmDialogOpen(false);
    setBulkActionType(null);
  }, [isBulkProcessing]);

  const handleConfirmBulkAction = useCallback(async () => {
    if (!selectedCustomerIds.length || !bulkActionType) return;

    setIsBulkProcessing(true);
    try {
      if (bulkActionType === 'delete') {
        for (const id of selectedCustomerIds) {
          await dispatch(deleteCustomer(id));
        }
      } else if (bulkActionType === 'block' || bulkActionType === 'unblock') {
        const isBlocked = bulkActionType === 'block';
        for (const id of selectedCustomerIds) {
          await dispatch(toggleCustomerBlocked({ id, isBlocked }));
        }
      }
      setSelectedCustomerIds([]);
      setBulkConfirmDialogOpen(false);
      setBulkActionType(null);
    } catch (error) {
      console.error('Fehler bei Bulk-Aktion:', error);
    } finally {
      setIsBulkProcessing(false);
    }
  }, [dispatch, selectedCustomerIds, bulkActionType]);

  const formatDate = useCallback((timestamp) => {
    if (!timestamp) return 'Nie';
    return DATE_FORMATTER.format(new Date(timestamp));
  }, []);

  // Filter und Sort – wird nur neu berechnet wenn customers oder Filterkriterien sich ändern.
  // Echtzeit-Updates vom Kunden-Status-Listener triggern automatisch eine Neuberechnung,
  // da `customers` als Dependency enthalten ist.
  const filteredData = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return customers.filter(customer => {
      const searchMatch =
        customer.user?.name?.toLowerCase().includes(term) ||
        customer.user?.email?.toLowerCase().includes(term) ||
        customer.user?.phoneNumber?.includes(searchTerm);
      const onlineMatch =
        onlineFilter === 'all' ||
        (onlineFilter === 'online' && customer.isOnline) ||
        (onlineFilter === 'offline' && !customer.isOnline);
      const customerTypeMatch =
        customerTypeFilter === 'all' ||
        (customerTypeFilter === 'phone' && customer.user?.isPhoneCustomer) ||
        (customerTypeFilter === 'app' && !customer.user?.isPhoneCustomer);
      const blockedMatch =
        blockedFilter === 'all' ||
        (blockedFilter === 'active' && !customer.isBlocked) ||
        (blockedFilter === 'blocked' && customer.isBlocked === true);
      return searchMatch && onlineMatch && customerTypeMatch && blockedMatch;
    }).sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.user?.name || '').localeCompare(b.user?.name || '');
        case 'rides':
          return (b.total_rides || 0) - (a.total_rides || 0);
        case 'lastLogin':
          return (b.lastLogin ? new Date(b.lastLogin).getTime() : 0) -
                 (a.lastLogin ? new Date(a.lastLogin).getTime() : 0);
        default:
          return 0;
      }
    });
  }, [customers, searchTerm, onlineFilter, customerTypeFilter, blockedFilter, sortBy]);

  // Pagination – neu berechnet nur wenn filteredData, page oder rowsPerPage sich ändern
  const paginatedData = useMemo(
    () => filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredData, page, rowsPerPage]
  );

  // Auswahl-Status der aktuell sichtbaren Seite
  const pageSelection = useMemo(() => {
    const pageIds = paginatedData.map(c => c.id);
    const selectedOnPage = pageIds.filter(id => selectedCustomerIds.includes(id));
    return {
      pageIds,
      allSelected: pageIds.length > 0 && selectedOnPage.length === pageIds.length,
      someSelected: selectedOnPage.length > 0 && selectedOnPage.length < pageIds.length,
    };
  }, [paginatedData, selectedCustomerIds]);

  const handleToggleSelectAllOnPage = useCallback(() => {
    if (pageSelection.allSelected) {
      setSelectedCustomerIds(prev => prev.filter(id => !pageSelection.pageIds.includes(id)));
    } else {
      setSelectedCustomerIds(prev => Array.from(new Set([...prev, ...pageSelection.pageIds])));
    }
  }, [pageSelection]);

  // Namen der ausgewählten Kunden für den Bestätigungsdialog
  const selectedCustomerNames = useMemo(() => {
    const byId = new Map(customers.map(c => [c.id, c.user?.name || 'Unbekannt']));
    return selectedCustomerIds.map(id => byId.get(id) || 'Unbekannt');
  }, [customers, selectedCustomerIds]);
  
  if (isLoading) {
    return <Loading message="Kunden werden geladen..." />;
  }
  
  if (error) {
    console.error('Fehler in CustomersList Component:', error);
    return (
      <ErrorMessage
        title="Fehler beim Laden der Kunden"
        message={error.message || error.details || JSON.stringify(error)}
        onRetry={() => dispatch(fetchCustomers())}
      />
    );
  }
  
  return (
    <Box sx={{ flexGrow: 1, p: { xs: 1.5, sm: 2, md: 3 } }}>
      <PageHeader
        title="Kundenverwaltung"
        description="Übersicht und Verwaltung aller Kunden"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Kunden', path: '/kunden' }
        ]}
        actionButton={{
          label: 'Kunde hinzufügen',
          icon: <AddIcon />,
          path: '/kunden/neu'
        }}
        secondaryButton={{
          label: 'Rechnungskunden',
          icon: <BusinessIcon />,
          path: '/rechnungskunden'
        }}
      />
      
      {/* Filter und Suche */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: { xs: 2, md: 3 } }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              fullWidth
              variant="outlined"
              label="Suche nach Name, Email oder Telefon"
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="online-filter-label">Online Status</InputLabel>
              <Select
                labelId="online-filter-label"
                id="online-filter"
                value={onlineFilter}
                label="Online Status"
                onChange={handleOnlineFilterChange}
              >
                <MenuItem value="all">Alle Status</MenuItem>
                <MenuItem value="online">Online</MenuItem>
                <MenuItem value="offline">Offline</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="customer-type-filter-label">Kundentyp</InputLabel>
              <Select
                labelId="customer-type-filter-label"
                id="customer-type-filter"
                value={customerTypeFilter}
                label="Kundentyp"
                onChange={handleCustomerTypeFilterChange}
              >
                <MenuItem value="all">Alle</MenuItem>
                <MenuItem value="phone">Telefonkunden</MenuItem>
                <MenuItem value="app">App-Kunden</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="blocked-filter-label">Kontostatus</InputLabel>
              <Select
                labelId="blocked-filter-label"
                id="blocked-filter"
                value={blockedFilter}
                label="Kontostatus"
                onChange={handleBlockedFilterChange}
              >
                <MenuItem value="all">Alle</MenuItem>
                <MenuItem value="active">Nur aktive</MenuItem>
                <MenuItem value="blocked">Nur gesperrte</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="sort-by-label">Sortieren nach</InputLabel>
              <Select
                labelId="sort-by-label"
                id="sort-by"
                value={sortBy}
                label="Sortieren nach"
                onChange={handleSortByChange}
              >
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="rides">Anzahl der Fahrten</MenuItem>
                <MenuItem value="lastLogin">Zuletzt Online</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 1 }}>
            <Typography variant="body2" color="textSecondary">
              {filteredData.length} Kunden
            </Typography>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Aktions-Toolbar für Mehrfachauswahl */}
      {selectedCustomerIds.length > 0 && (
        <Toolbar
          sx={{
            pl: 2,
            pr: 2,
            mb: 1,
            bgcolor: 'action.selected',
            borderRadius: 1,
            gap: 1,
            flexWrap: 'wrap',
            '& .MuiButton-root': {
              flex: { xs: '1 1 calc(50% - 8px)', sm: '0 0 auto' },
            },
          }}
        >
          <Typography sx={{ flex: '1 1 100%' }} variant="subtitle1">
            {selectedCustomerIds.length} Kunde{selectedCustomerIds.length !== 1 ? 'n' : ''} ausgewählt
          </Typography>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={() => setSelectedCustomerIds([])}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Auswahl aufheben
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="warning"
            startIcon={<BlockIcon />}
            onClick={() => handleBulkAction('block')}
            disabled={!canManageSelectedCustomers}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Sperren
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="success"
            startIcon={<UnblockIcon />}
            onClick={() => handleBulkAction('unblock')}
            disabled={!canManageSelectedCustomers}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Entsperren
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => handleBulkAction('delete')}
            disabled={!canManageSelectedCustomers}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Löschen
          </Button>
        </Toolbar>
      )}

      {/* Mobile Kundenkarten */}
      <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.5 }}>
        {paginatedData.length > 0 ? (
          paginatedData.map((customer) => {
            const customerName = customer.user?.name || 'Unbekannter Kunde';
            const isSelected = selectedCustomerIds.includes(customer.id);
            return (
              <Card key={customer.id} variant="outlined" sx={{ bgcolor: isSelected ? 'action.selected' : 'background.paper' }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start' }}>
                    <Box sx={{ display: 'flex', gap: 1.25, minWidth: 0 }}>
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleToggleCustomerSelect(customer.id)}
                        inputProps={{ 'aria-label': `Kunde ${customerName} auswählen` }}
                        sx={{ p: 0.5, alignSelf: 'flex-start' }}
                      />
                      <Avatar
                        src={customer.user?.profile_picture || ''}
                        sx={{ opacity: customer.isBlocked ? 0.5 : 1 }}
                      >
                        {customer.user?.name ? customer.user.name.charAt(0) : 'K'}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle2" sx={{ overflowWrap: 'anywhere' }}>
                            {customerName}
                          </Typography>
                          <Chip
                            icon={customer.user?.isPhoneCustomer ? <PhoneIcon fontSize="small" /> : <SmartphoneIcon fontSize="small" />}
                            label={customer.user?.isPhoneCustomer ? 'Telefon' : 'App'}
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 22,
                              fontSize: '0.7rem',
                              borderColor: customer.user?.isPhoneCustomer ? 'warning.main' : 'success.main',
                              color: customer.user?.isPhoneCustomer ? 'warning.dark' : 'success.dark',
                              '& .MuiChip-icon': {
                                color: customer.user?.isPhoneCustomer ? 'warning.dark' : 'success.dark',
                                fontSize: '0.8rem',
                              },
                            }}
                          />
                          {customer.isBlocked && (
                            <Chip
                              label="Gesperrt"
                              size="small"
                              icon={<BlockIcon />}
                              color="error"
                              variant="outlined"
                              sx={{ height: 22, fontSize: '0.7rem' }}
                            />
                          )}
                          {customer.user?.invoiceCustomerId && (
                            <Chip
                              label={`Rechnungskunde${customer.user?.invoiceCustomerName ? ': ' + customer.user.invoiceCustomerName : ''}`}
                              size="small"
                              icon={<BusinessIcon />}
                              variant="outlined"
                              component={Link}
                              to="/rechnungskunden"
                              clickable
                              sx={{ height: 22, fontSize: '0.7rem', borderColor: 'info.main', color: 'info.dark', '& .MuiChip-icon': { color: 'info.dark', fontSize: '0.8rem' } }}
                            />
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                          {customer.user?.email || 'Keine E-Mail'}
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton
                      aria-label="Aktionen"
                      aria-controls={`customer-menu-${customer.id}`}
                      aria-haspopup="true"
                      onClick={(e) => handleOpenMenu(e, customer)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>

                  <Divider sx={{ my: 1.25 }} />

                  <Grid container spacing={1}>
                    <Grid size={6}>
                      <Typography variant="caption" color="text.secondary">Online</Typography>
                      <Box sx={{ mt: 0.25 }}>
                        <Chip
                          label={customer.isOnline ? 'Online' : 'Offline'}
                          color={customer.isOnline ? 'success' : 'default'}
                          size="small"
                          icon={customer.isOnline ? <OnlineIcon /> : <OfflineIcon />}
                        />
                      </Box>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="caption" color="text.secondary">Fahrten</Typography>
                      <RidesBox sx={{ mt: 0.25 }}>
                        <RidesIcon />
                        <Typography variant="body2">{customer.total_rides || 0}</Typography>
                      </RidesBox>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="caption" color="text.secondary">{loyaltyColumnLabel}</Typography>
                      <Box sx={{ mt: 0.25, display: 'flex', justifyContent: 'flex-start' }}>
                        {renderLoyaltyStatus(customer)}
                      </Box>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="caption" color="text.secondary">Zuletzt online</Typography>
                      <Typography variant="body2">{formatDate(customer.lastLogin)}</Typography>
                    </Grid>
                    <Grid size={12}>
                      <Typography variant="caption" color="text.secondary">Telefon</Typography>
                      <Typography variant="body2">{customer.user?.phoneNumber || 'Nicht angegeben'}</Typography>
                    </Grid>
                  </Grid>

                  <Button
                    fullWidth
                    variant="outlined"
                    size="small"
                    component={Link}
                    to={`/kunden/${customer.id}`}
                    sx={{ mt: 1.5 }}
                  >
                    Details anzeigen
                  </Button>
                </CardContent>
              </Card>
            );
          })
        ) : (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1">Keine Kunden gefunden</Typography>
          </Paper>
        )}
        <TablePagination
          rowsPerPageOptions={[10, 25]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Pro Seite:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} von ${count}`}
          sx={{
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            '.MuiTablePagination-toolbar': {
              px: 1,
              flexWrap: 'wrap',
              justifyContent: 'center',
            },
          }}
        />
      </Box>

      {/* Desktop Kundentabelle */}
      <TableContainer component={Paper} sx={{ display: { xs: 'none', md: 'block' } }}>
        <Table sx={{ minWidth: 650 }} aria-label="Kundentabelle">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={pageSelection.someSelected}
                  checked={pageSelection.allSelected}
                  onChange={handleToggleSelectAllOnPage}
                  disabled={paginatedData.length === 0}
                  inputProps={{ 'aria-label': 'Alle auf dieser Seite auswählen' }}
                />
              </TableCell>
              <TableCell>Kunde</TableCell>
              <TableCell align="center">Online</TableCell>
              <TableCell align="center">Fahrten</TableCell>
              <TableCell align="center">{loyaltyColumnLabel}</TableCell>
              <TableCell align="center">Telefon</TableCell>
              <TableCell align="center">Zuletzt Online</TableCell>
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.length > 0 ? (
              paginatedData.map((customer) => (
                <StyledTableRow
                  key={customer.id}
                  selected={selectedCustomerIds.includes(customer.id)}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedCustomerIds.includes(customer.id)}
                      onChange={() => handleToggleCustomerSelect(customer.id)}
                      inputProps={{ 'aria-label': `Kunde ${customer.user?.name || ''} auswählen` }}
                    />
                  </TableCell>
                  <TableCell component="th" scope="row">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        src={customer.user?.profile_picture || ''}
                        sx={{ opacity: customer.isBlocked ? 0.5 : 1 }}
                      >
                        {customer.user?.name ? customer.user.name.charAt(0) : 'K'}
                      </Avatar>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle2">{customer.user?.name}</Typography>
                          <Chip
                            icon={customer.user?.isPhoneCustomer ? <PhoneIcon fontSize="small" /> : <SmartphoneIcon fontSize="small" />}
                            label={customer.user?.isPhoneCustomer ? 'Telefon' : 'App'}
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              borderColor: customer.user?.isPhoneCustomer ? 'warning.main' : 'success.main',
                              color: customer.user?.isPhoneCustomer ? 'warning.dark' : 'success.dark',
                              '& .MuiChip-icon': {
                                color: customer.user?.isPhoneCustomer ? 'warning.dark' : 'success.dark',
                                fontSize: '0.8rem',
                              },
                            }}
                          />
                          {customer.isBlocked && (
                            <Chip
                              label="Gesperrt"
                              size="small"
                              icon={<BlockIcon />}
                              color="error"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.65rem' }}
                            />
                          )}
                          {customer.user?.invoiceCustomerId && (
                            <Tooltip title={customer.user?.invoiceCustomerName || 'Rechnungskunde'}>
                              <Chip
                                label={`Rechnungskunde${customer.user?.invoiceCustomerName ? ': ' + customer.user.invoiceCustomerName : ''}`}
                                size="small"
                                icon={<BusinessIcon />}
                                variant="outlined"
                                component={Link}
                                to="/rechnungskunden"
                                clickable
                                sx={{ height: 20, fontSize: '0.65rem', maxWidth: 220, borderColor: 'info.main', color: 'info.dark', '& .MuiChip-icon': { color: 'info.dark', fontSize: '0.8rem' } }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                        <Typography variant="body2" color="textSecondary">{customer.user?.email}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={customer.isOnline ? "Online" : "Offline"}
                      color={customer.isOnline ? "success" : "default"}
                      size="small"
                      icon={customer.isOnline ? <OnlineIcon /> : <OfflineIcon />}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <RidesBox>
                      <RidesIcon />
                      <Typography>{customer.total_rides || 0}</Typography>
                    </RidesBox>
                  </TableCell>
                  <TableCell align="center">
                    {renderLoyaltyStatus(customer)}
                  </TableCell>
                  <TableCell align="center">{customer.user?.phoneNumber}</TableCell>
                  <TableCell align="center">{formatDate(customer.lastLogin)}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      aria-label="Aktionen"
                      aria-controls={`customer-menu-${customer.id}`}
                      aria-haspopup="true"
                      onClick={(e) => handleOpenMenu(e, customer)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </StyledTableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body1" sx={{ py: 3 }}>
                    Keine Kunden gefunden
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Zeilen pro Seite:"
          labelDisplayedRows={
            ({ from, to, count }) => `${from}–${to} von ${count}`
          }
        />
      </TableContainer>
      
      {/* Aktions-Menü */}
      <Menu
        id={menuCustomer ? `customer-menu-${menuCustomer.id}` : undefined}
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem 
          component={Link} 
          to={menuCustomer ? `/kunden/${menuCustomer.id}` : '#'} 
          onClick={handleCloseMenu}
        >
          Kunde anzeigen
        </MenuItem>
        {menuCustomer && canManageCustomer(menuCustomer) && (
          <MenuItem
            component={Link}
            to={`/kunden/${menuCustomer.id}/bearbeiten`}
            onClick={handleCloseMenu}
          >
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Bearbeiten
          </MenuItem>
        )}
        {menuCustomer && canManageCustomer(menuCustomer) && (
          <MenuItem
            onClick={() => handleToggleBlocked(menuCustomer)}
            sx={{ color: menuCustomer?.isBlocked ? 'success.main' : 'warning.main' }}
          >
            {menuCustomer?.isBlocked ? (
              <><UnblockIcon fontSize="small" sx={{ mr: 1 }} />Entsperren</>
            ) : (
              <><BlockIcon fontSize="small" sx={{ mr: 1 }} />Sperren</>
            )}
          </MenuItem>
        )}
        {menuCustomer && canManageCustomer(menuCustomer) && (
          <MenuItem
            onClick={() => handleDeleteClick(menuCustomer)}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Deaktivieren
          </MenuItem>
        )}
      </Menu>
      
      {/* Löschen-Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        title="Kunde löschen"
        content={`Möchten Sie den Kunden ${selectedCustomer?.user?.name || ''} wirklich löschen? Dieser Vorgang kann nicht rückgängig gemacht werden.`}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />

      {/* Bestätigungs-Dialog für Bulk-Aktionen */}
      <Dialog
        open={bulkConfirmDialogOpen}
        onClose={handleCloseBulkDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedCustomerIds.length} Kunde{selectedCustomerIds.length !== 1 ? 'n' : ''}{' '}
          {bulkActionType === 'delete' && 'löschen'}
          {bulkActionType === 'block' && 'sperren'}
          {bulkActionType === 'unblock' && 'entsperren'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Sind Sie sicher, dass Sie {selectedCustomerIds.length} ausgewählte{selectedCustomerIds.length !== 1 ? '' : 'n'} Kunde{selectedCustomerIds.length !== 1 ? 'n' : ''}{' '}
            {bulkActionType === 'delete' && 'löschen'}
            {bulkActionType === 'block' && 'sperren'}
            {bulkActionType === 'unblock' && 'entsperren'}{' '}
            möchten?
          </Typography>

          {selectedCustomerIds.length > 1 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Hinweis: Diese Aktion wird auf <strong>{selectedCustomerIds.length} Kunden gleichzeitig</strong> angewendet.
            </Alert>
          )}

          {bulkActionType === 'delete' && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Die ausgewählten Kunden werden deaktiviert. Dieser Vorgang kann nicht rückgängig gemacht werden.
            </Alert>
          )}

          {selectedCustomerNames.length > 0 && selectedCustomerNames.length <= 10 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Betroffene Kunden:
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedCustomerNames.join(', ')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBulkDialog} disabled={isBulkProcessing}>
            Abbrechen
          </Button>
          <Button
            onClick={handleConfirmBulkAction}
            disabled={isBulkProcessing}
            color={bulkActionType === 'unblock' ? 'success' : bulkActionType === 'block' ? 'warning' : 'error'}
            variant="contained"
          >
            {bulkActionType === 'delete' && 'Löschen'}
            {bulkActionType === 'block' && 'Sperren'}
            {bulkActionType === 'unblock' && 'Entsperren'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomersList;
